import { Keyboard } from '@capacitor/keyboard';
import { yupResolver } from '@hookform/resolvers/yup';
import { IonButton, IonCard, IonCol, IonContent, IonIcon, IonItem, IonLabel, IonRow, IonThumbnail } from '@ionic/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Capacitor } from '@capacitor/core';
import { unwrapResult } from '@reduxjs/toolkit';
import { business, eye, eyeOff, lockClosed, person, server } from 'ionicons/icons';
import { ICompany } from '../../../models/portal/company.model';
import { IDatabase } from '../../../models/portal/database.model';
import { setSelectedDealerCodeState, setSelectedDealerCodeStoreAsync, signInAsync, signOutAsync } from '../../../store/authSlice';
import { fetchCompanyByDealerCodeAsync, setSelectedCompanyState, setSelectedCompanyStoreAsync } from '../../../store/companySlice';
import { fetchDatabasesByCompanyNumberAsync, setDatabasesSearchState, setSelectedDatabaseState, setSelectedDatabaseStoreAsync } from '../../../store/databaseSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setSelectedUsernameState, setSelectedUsernameStoreAsync } from '../../../store/userSlice';
import { IonPageWrapper } from './CompanyPage.styles';
import FormInput from './formInputs/formInput/FormInput';
import FormSelectable from './formInputs/loginSelectable/FormSelectable';
import { reInitialisePortalAxios } from '../../../api/portal/axiosPortalInstance';
import { reInitialiseDmsAxios } from '../../../api/dms/axiosDmsInstance';
import { IAppConfigType } from '../../../models/appConfig.model';
import OtpInput from '../../../components/otpInput/OtpInput';
import { IJwt } from '../../../models/portal/auth.model';
import { useToast } from '../../../providers/ToastProvider';
interface IComponentProps {}

interface IFormInputs {
  dealerCode: string;
  database: IDatabase | undefined;
  username: string;
  password: string;
  otp: string;
}

const schema = yup.object().shape({
  dealerCode: yup.string().required('dealer code is required').length(8, 'dealer code is 8 characters'),
  database: yup.object().required('database is required'),
  username: yup.string().required('username is required'),
  password: yup.string().required('password is required'),
  otp: yup.string(),
});

const CompanyPage: React.FC<IComponentProps> = () => {
  const dispatch = useAppDispatch();

  const [showDatabaseModalLoading, setShowDatabaseModalLoading] = useState(false);

  const { selectedCompany, selectedCompanyLoading } = useAppSelector((state) => state.company);
  const { databasesSearch, databasesSearchLoading, selectedDatabase } = useAppSelector((state) => state.database);
  const { selectedUsername } = useAppSelector((state) => state.user);
  const { isAuthenticating, selectedDealerCode } = useAppSelector((state) => state.auth);
  const { isDemo } = useAppSelector((state) => state.app);
  const [showPassword, setShowPassword] = useState(false);
  const [showOtp, setShowOtp] = useState(false);

  const {
    actions: { errorToast },
  } = useToast();

  const { control, handleSubmit, reset, setValue } = useForm<IFormInputs>({
    defaultValues: {
      dealerCode: selectedDealerCode,
      database: selectedDatabase,
      username: selectedUsername,
      password: '',
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const fetchDatabases = async (dealerCode: string) => {
    if (!databasesSearch.length && selectedDatabase?.dbId) {
      dispatch(setDatabasesSearchState([selectedDatabase]));
    }
    setShowDatabaseModalLoading(true);
    await dispatch(fetchDatabasesByCompanyNumberAsync(dealerCode));
    setShowDatabaseModalLoading(false);
  };

  useEffect(() => {
    if (isDemo) {
      const demoCompany = { id: 1, code: 'Sandbox Motors', name1: 'Sandbox Motors' } as ICompany;
      const demoDb = { dbId: 1, dbDisplayName: 'Sandbox' } as IDatabase;
      setValue('database', demoDb);
      setValue('username', 'usupport');
      setValue('password', '1234567890');
      setValue('dealerCode', 'a1b2c3d4');
      dispatch(setSelectedCompanyStoreAsync(demoCompany));
      dispatch(setSelectedDatabaseStoreAsync(demoDb));
      dispatch(setDatabasesSearchState([demoDb]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isDemo) {
      setValue('database', selectedDatabase);
      setValue('dealerCode', selectedDealerCode);

      if (selectedDealerCode && !databasesSearch.length && selectedDatabase?.dbId) {
        dispatch(setDatabasesSearchState([selectedDatabase]));
        fetchDatabases(selectedDealerCode);
      }
    }

    // Auto login
    const autologin = selectedCompany?.configs?.find((c) => c.name === 'autologin')?.value;
    if (autologin) {
      setValue('password', autologin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databasesSearch, selectedCompany, selectedDatabase, selectedDealerCode]);

  const handleDealerCodeChange = async (dealerCode: string | number | null | undefined) => {
    dealerCode = (dealerCode || '').toString();
    if (dealerCode.length === 8) {
      await dispatch(setSelectedDealerCodeStoreAsync(dealerCode));
      // Reset company and database selection and search state
      await dispatch(setSelectedCompanyStoreAsync({} as ICompany));
      await dispatch(setSelectedDatabaseStoreAsync({} as IDatabase));
      dispatch(setDatabasesSearchState([]));
      // Fetch company info
      await reInitialiseDmsAxios(IAppConfigType.PROD);
      await reInitialisePortalAxios(IAppConfigType.PROD);
      const resultAction = await dispatch(fetchCompanyByDealerCodeAsync(dealerCode));
      const company = unwrapResult(resultAction) as ICompany | undefined;

      if (company?.code) {
        // Load databases from dealer code
        await dispatch(setSelectedCompanyStoreAsync(company));

        const isTest = (company.configs?.find((c) => c.name === process.env.REACT_APP_TESTCONFIG)?.value ?? 'N') === 'Y' ? true : false;
        const isPilot = (company.configs?.find((c) => c.name === process.env.REACT_APP_PILOTCONFIG)?.value ?? 'N') === 'Y' ? true : false;
        await reInitialiseDmsAxios(isTest ? IAppConfigType.TEST : isPilot ? IAppConfigType.PILOT : IAppConfigType.PROD);
        await reInitialisePortalAxios(isTest ? IAppConfigType.TEST : isPilot ? IAppConfigType.PILOT : IAppConfigType.PROD);

        await fetchDatabases(dealerCode);
      }
    }
  };

  // Database selectable handlers
  const handleDatabaseSelection = async (database: IDatabase) => {
    await dispatch(setSelectedDatabaseStoreAsync(database));
  };

  const resetForm = (isShowOtp: boolean) => {
    setShowOtp(false);

    if (!isShowOtp) {
      dispatch(setSelectedCompanyState({} as ICompany));
      dispatch(setSelectedDatabaseState({} as IDatabase));
      dispatch(setSelectedDealerCodeState(''));
      dispatch(setSelectedUsernameState(''));
      setValue('username', '');
      setValue('password', '');
      reset();
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
      Keyboard.hide();
    }
    try {
      await dispatch(setSelectedUsernameStoreAsync(data.username));
      const resultActionSignIn = await dispatch(signInAsync({ username: data.username, password: data.password, databaseId: data.database?.dbId || 0, otp: data.otp }));
      const token = unwrapResult(resultActionSignIn) as IJwt;
      if (token.error_description === 'The TOTP is not provided.') {
        setShowOtp(true);
      } else if (token.error_description === 'Invalid Credentials') {
        errorToast('Provided credentials are invalid');
      }
    } catch (ex) {
      dispatch(signOutAsync());
    }
  });

  return (
    <IonPageWrapper>
      <IonContent>
        <IonCard className="login-card">
          <IonRow className="app-title ion-text-center">
            <IonThumbnail className="logo">
              <img src="assets/icon/icon.png" alt="Tech Mobile Icon" />
            </IonThumbnail>
            <IonLabel>Tech Mobile</IonLabel>
          </IonRow>
          <IonRow className="ion-padding-top">
            <IonCol className="ion-padding-top">
              <form id="companyForm" noValidate onSubmit={onSubmit}>
                {!showOtp && (
                  <>
                    <IonItem className="input-group" lines="none">
                      <FormInput control={control} name="dealerCode" placeholder="Dealer Code" maxLength={8} handleChange={(dc) => handleDealerCodeChange(dc)} hasLine={false} icon={business} iconColor="white" />
                    </IonItem>
                    <IonItem className="input-group" lines="none">
                      <FormSelectable
                        items={databasesSearch}
                        placeholder="Database"
                        handleSelection={handleDatabaseSelection}
                        showLoading={showDatabaseModalLoading}
                        name="database"
                        iconColor="white"
                        control={control}
                        fieldKey="dbId"
                        itemKey="dbId"
                        itemValue="dbDisplayName"
                        itemsLoading={databasesSearchLoading || selectedCompanyLoading}
                        hasLine={false}
                        icon={server}
                      />
                    </IonItem>
                    <IonItem className="input-group" lines="none">
                      <FormInput control={control} placeholder="Username" name="username" hasLine={false} icon={person} iconColor="white" />
                    </IonItem>
                    <IonItem className="input-group" lines="none">
                      <FormInput control={control} type={showPassword ? 'text' : 'password'} placeholder="Password" name="password" hasLine={false} />
                      <IonButton fill="clear" onClick={() => setShowPassword((prev) => !prev)}>
                        <IonIcon color="light" icon={showPassword ? eyeOff : eye} />
                      </IonButton>
                      <IonIcon color="light" className="field-icons" icon={lockClosed} />
                    </IonItem>
                  </>
                )}
                {showOtp && <OtpInput theme="dark" changeCallback={(otp) => setValue('otp', otp)} />}
                <IonRow className="row-button">
                  <button type="submit" className="hide" />
                  <IonButton type="submit" disabled={isAuthenticating} className="flexed-fill">
                    <span className="label">SIGN IN</span>
                  </IonButton>
                  <IonButton onClick={() => resetForm(showOtp)}>{showOtp ? <span>BACK</span> : <span>RESET</span>}</IonButton>
                </IonRow>
              </form>
            </IonCol>
          </IonRow>
        </IonCard>
      </IonContent>
    </IonPageWrapper>
  );
};

export default CompanyPage;
